import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BrainCircuit, FolderDown, LayoutDashboard, Library, PlayCircle, TrendingUp } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section, SectionHeading } from "@/components/site/Section";
import { EmptyState, Loading, ProblemAlert } from "@/components/site/Feedback";
import { MembershipPlanCard } from "@/components/site/Membership";
import { api } from "@/lib/api";
import { duration } from "@/lib/format";
import type { MemberVideoDto } from "@/lib/types";
import { cn } from "@/lib/utils";

const portalFeatures = [
  {
    icon: LayoutDashboard,
    title: "Main dashboard",
    body: "Your welcome screen with quick links to the Analyzer and visible progress tracking.",
  },
  {
    icon: Library,
    title: "Video library",
    body: "Segmented by investor level — Bronze foundations, Silver growth, Gold scaling.",
  },
  {
    icon: BrainCircuit,
    title: "AI Deal Analyzer",
    body: "Ask about any listing for refurb estimates, rental yields and drafted offer letters.",
  },
  {
    icon: FolderDown,
    title: "Resource hub",
    body: "Downloadable checklists, tenant templates and legal document structures.",
  },
  {
    icon: TrendingUp,
    title: "Upgrade centre",
    body: "Book 1-on-1 strategy hours or move up to a Bronze, Silver, Gold or VIP scheme.",
  },
];

const directVideo = /\.(mp4|webm|ogg|mov)(\?|#|$)/i;

/** Cloudflare Stream / external player URLs are embedded as iframes; direct files use <video>. */
function VideoPlayer({ video }: { video: MemberVideoDto }) {
  if (!video.playerUrl) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-lg bg-muted text-sm text-muted-foreground">
        This video is being prepared.
      </div>
    );
  }
  if (directVideo.test(video.playerUrl)) {
    return (
      <video
        key={video.id}
        src={video.playerUrl}
        poster={video.thumbnailUrl ?? undefined}
        controls
        playsInline
        className="aspect-video w-full rounded-lg bg-ink"
      />
    );
  }
  return (
    <div className="relative aspect-video overflow-hidden rounded-lg bg-ink">
      <iframe
        key={video.id}
        src={video.playerUrl}
        title={video.name}
        className="absolute inset-0 size-full border-0"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
      />
    </div>
  );
}

export function AcademyPage() {
  usePageTitle("Academy Portal");
  const videos = useQuery({ queryKey: ["academy", "videos"], queryFn: api.academy.videos });
  const list = [...(videos.data?.videos ?? [])].sort((a, b) => a.order - b.order);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = list.find((v) => v.id === selectedId) ?? list[0];

  useEffect(() => {
    if (selectedId && !list.some((v) => v.id === selectedId)) setSelectedId(null);
  }, [list, selectedId]);

  return (
    <>
      <PageHero
        eyebrow="Members' area"
        title="The Academy Portal"
        subtitle="The full video library, segmented by investor level, plus the AI Deal Analyzer — everything behind the members' door."
      >
        <Button asChild variant="gold" size="xl">
          <Link to="/deal-analyzer">Open the Deal Analyzer</Link>
        </Button>
        <Button asChild variant="outlineLight" size="xl">
          <Link to="/dashboard">My dashboard</Link>
        </Button>
      </PageHero>

      <Section>
        {videos.isPending ? (
          <Loading label="Loading the video library…" />
        ) : videos.isError ? (
          <ProblemAlert error={videos.error} />
        ) : videos.data.subscriptionRequired ? (
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <SectionHeading
              eyebrow="Membership"
              title="Unlock the video library"
              subtitle="The Academy videos are part of the membership. Subscribe to watch every lesson, in order, at your own pace."
            />
            <MembershipPlanCard />
          </div>
        ) : list.length === 0 ? (
          <EmptyState title="New lessons are on the way">The first videos will appear here very soon.</EmptyState>
        ) : (
          <>
            <SectionHeading eyebrow="Video library" title="Watch the lessons in order" />
            <div className="mt-12 grid gap-8 lg:grid-cols-[1.6fr_1fr]">
              {selected && (
                <div>
                  <VideoPlayer video={selected} />
                  <h3 className="mt-6 text-2xl">{selected.name}</h3>
                  {selected.description && (
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{selected.description}</p>
                  )}
                </div>
              )}
              <ol className="h-fit divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-card">
                {list.map((v, i) => (
                  <li key={v.id}>
                    <button
                      onClick={() => setSelectedId(v.id)}
                      className={cn(
                        "flex w-full items-start gap-4 px-5 py-4 text-left transition-colors hover:bg-accent/60",
                        selected?.id === v.id && "bg-accent",
                      )}
                    >
                      <span className="font-display text-2xl leading-none text-gold">{String(i + 1).padStart(2, "0")}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{v.name}</span>
                        {v.durationSeconds ? (
                          <span className="mt-1 block text-xs text-muted-foreground">{duration(v.durationSeconds)}</span>
                        ) : null}
                      </span>
                      <PlayCircle className={cn("size-4 shrink-0", selected?.id === v.id ? "text-gold" : "text-muted-foreground")} />
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          </>
        )}
      </Section>

      <Section ink className="py-20">
        <SectionHeading light eyebrow="What's inside" title="Everything behind the members' door" />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {portalFeatures.map((f) => (
            <div key={f.title} className="rounded-lg border border-ink-foreground/15 bg-ink-foreground/5 p-7">
              <f.icon className="size-6 text-gold" />
              <h3 className="mt-5 text-xl text-ink-foreground">{f.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-foreground/70">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}
